"use client";

import * as React from "react";
import { createPortal } from "react-dom";

/**
 * Update Multiple for Travel Reimbursement.
 * Bulk-sets mileage/per diem flags and optional override miles on selected employees.
 * Persists when the user clicks Save and Log Production on the parent modal.
 */

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="close-icon"
      style={{
        aspectRatio: "1/1",
        fill: "#424143",
        position: "relative",
        width: "18px",
        height: "18px",
      }}
    >
      <path
        d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z"
        fill="#424143"
      />
    </svg>
  );
}

function EmployeeCheckbox({ name, checked = false, onChange }) {
  return (
    <label className="flex gap-2 items-center bg-white rounded-sm cursor-pointer">
      <div className="relative h-[18px] w-[18px]">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange?.(e.target.checked)}
          className="absolute top-0 left-0 rounded-sm border border-solid border-zinc-300 h-[18px] w-[18px] cursor-pointer"
        />
      </div>
      <span className="font-poppins text-[14px] font-normal text-[#2C2C2C]">
        {name}
      </span>
    </label>
  );
}

const PARTIAL_MILES_INPUT_RE = /^\d*\.?\d*$/;
const isValidPartialMilesInput = (value) =>
  value === "" || PARTIAL_MILES_INPUT_RE.test(value);

/**
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   employees: Array<{ id: string, name: string }>,
 *   mileageTypes: Array<{ id: string, label: string }>,
 *   perDiemTypes: Array<{ id: string, label: string }>,
 *   defaultMiles: string|number,
 *   onApply: (payload: {
 *     employeeIds: string[],
 *     flagsPatch: Record<string, boolean>,
 *     milesValue: string|null,
 *     setOverride: boolean|null,
 *   }) => void,
 * }} props
 */
function CrewTravelUpdateMultipleModal({
  isOpen,
  onClose,
  employees = [],
  mileageTypes = [],
  perDiemTypes = [],
  defaultMiles = "0",
  onApply,
}) {
  const [selectedEmployeeIds, setSelectedEmployeeIds] = React.useState({});
  const [flagValues, setFlagValues] = React.useState({});
  const [overrideMiles, setOverrideMiles] = React.useState(false);
  const [milesValue, setMilesValue] = React.useState("");
  const [saveMessage, setSaveMessage] = React.useState(null);

  React.useEffect(() => {
    if (!isOpen) return;
    setSelectedEmployeeIds({});
    setSaveMessage(null);
    setOverrideMiles(false);
    setMilesValue(String(defaultMiles ?? "0"));
    const initial = {};
    [...mileageTypes, ...perDiemTypes].forEach((col) => {
      initial[col.id] = false;
    });
    setFlagValues(initial);
  }, [isOpen, employees, mileageTypes, perDiemTypes, defaultMiles]);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  const selectAllRef = React.useRef(null);

  const selectedEmployees = React.useMemo(
    () => (employees || []).filter((emp) => !!selectedEmployeeIds[emp.id]),
    [employees, selectedEmployeeIds]
  );
  const selectedCount = selectedEmployees.length;
  const allSelected =
    employees.length > 0 && selectedCount === employees.length;
  const someSelected = selectedCount > 0 && !allSelected;

  React.useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const handleEmployeeSelectionChange = (id, checked) => {
    setSelectedEmployeeIds((prev) => ({ ...prev, [id]: checked }));
    setSaveMessage(null);
  };

  const handleToggleAll = (checked) => {
    if (!checked) {
      setSelectedEmployeeIds({});
    } else {
      const next = {};
      (employees || []).forEach((emp) => {
        next[emp.id] = true;
      });
      setSelectedEmployeeIds(next);
    }
    setSaveMessage(null);
  };

  const handleFlagChange = (flagId, checked) => {
    setFlagValues((prev) => ({ ...prev, [flagId]: checked }));
    setSaveMessage(null);
  };

  const handleApply = () => {
    if (selectedCount === 0) {
      setSaveMessage({ type: "error", text: "Select at least one employee." });
      return;
    }

    onApply?.({
      employeeIds: selectedEmployees.map((e) => String(e.id)),
      flagsPatch: { ...flagValues },
      milesValue: overrideMiles ? milesValue : null,
      setOverride: overrideMiles ? true : null,
    });

    onClose?.();
  };

  if (!isOpen) return null;

  const canUpdate = selectedCount > 0;

  return createPortal(
    <div className="fixed inset-0 z-[10220] bg-black/50">
      <div className="absolute inset-2 lg:inset-6 flex min-h-0 justify-center">
        <div className="flex h-full max-h-full min-h-0 w-full max-w-[900px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
          <header className="relative z-10 flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 bg-white px-6 py-[18px] rounded-t-xl shadow-[0_2px_5px_rgba(0,0,0,0.10)]">
            <h2 className="font-poppins text-[20px] font-bold text-[#2C2C2C]">
              Update Multiple — Travel
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center w-9 h-9 rounded-md hover:bg-gray-100"
              aria-label="Close modal"
            >
              <CloseIcon />
            </button>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-4 sm:p-6 [-webkit-overflow-scrolling:touch]">
            {employees.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                No employees on this crew timesheet yet. Add employees first.
              </div>
            ) : (
              <>
                <section>
                  <h2 className="text-base font-semibold text-neutral-700 mb-4">
                    Select Employee
                  </h2>
                  <label className="mb-4 flex w-fit gap-2 items-center cursor-pointer">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => handleToggleAll(e.target.checked)}
                      aria-label="Select all employees"
                      className="rounded-sm border border-solid border-zinc-300 h-[18px] w-[18px] shrink-0 cursor-pointer"
                    />
                    <span className="font-poppins text-[14px] font-semibold text-[#2C2C2C]">
                      Select all
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-4 content-center items-center max-sm:gap-3">
                    {employees.map((employee) => (
                      <EmployeeCheckbox
                        key={employee.id}
                        name={employee.name}
                        checked={!!selectedEmployeeIds[employee.id]}
                        onChange={(checked) =>
                          handleEmployeeSelectionChange(employee.id, checked)
                        }
                      />
                    ))}
                  </div>
                </section>

                <section className="mt-6">
                  <h2 className="text-base font-semibold text-neutral-700 mb-3">
                    Miles
                  </h2>
                  <label className="flex items-center gap-2 mb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={overrideMiles}
                      onChange={(e) => {
                        setOverrideMiles(e.target.checked);
                        setSaveMessage(null);
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-[#AD2226] focus:ring-[#AD2226]"
                    />
                    <span className="text-sm text-gray-800">
                      Override Default Miles for selected employees
                    </span>
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    disabled={!overrideMiles}
                    value={milesValue}
                    onChange={(e) => {
                      if (!isValidPartialMilesInput(e.target.value)) return;
                      setMilesValue(e.target.value);
                      setSaveMessage(null);
                    }}
                    onFocus={(e) => e.target.select()}
                    className={`h-10 w-40 rounded-md border px-3 text-sm ${
                      overrideMiles
                        ? "border-gray-300 bg-white text-gray-900"
                        : "border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
                    }`}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Default from branch: {defaultMiles}
                  </p>
                </section>

                <section className="mt-6">
                  <h2 className="text-base font-semibold text-neutral-700 mb-3">
                    Mileage Reimbursement
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {mileageTypes.map((col) => (
                      <label
                        key={col.id}
                        className="flex items-center gap-2 rounded-md border border-gray-200 bg-[#EAF4FB]/px-3 py-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(flagValues[col.id])}
                          onChange={(e) =>
                            handleFlagChange(col.id, e.target.checked)
                          }
                          className="h-4 w-4 rounded border-gray-300 text-[#AD2226] focus:ring-[#AD2226]"
                        />
                        <span className="text-sm text-gray-800">{col.label}</span>
                      </label>
                    ))}
                  </div>
                </section>

                <section className="mt-6">
                  <h2 className="text-base font-semibold text-neutral-700 mb-3">
                    Per Diem
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {perDiemTypes.map((col) => (
                      <label
                        key={col.id}
                        className="flex items-center gap-2 rounded-md border border-gray-200 bg-[#FAF0E4]/px-3 py-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(flagValues[col.id])}
                          onChange={(e) =>
                            handleFlagChange(col.id, e.target.checked)
                          }
                          className="h-4 w-4 rounded border-gray-300 text-[#AD2226] focus:ring-[#AD2226]"
                        />
                        <span className="text-sm text-gray-800">{col.label}</span>
                      </label>
                    ))}
                  </div>
                </section>

                <div className="mt-8 mb-2">
                  {saveMessage && (
                    <p
                      className={`mb-3 text-sm ${
                        saveMessage.type === "error"
                          ? "text-red-600"
                          : "text-green-700"
                      }`}
                    >
                      {saveMessage.text}
                    </p>
                  )}
                  <button
                    type="button"
                    disabled={!canUpdate}
                    onClick={handleApply}
                    className="h-10 px-6 rounded-md bg-[#AD2226] text-sm font-medium text-white hover:bg-[#8F1B1E] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Apply to Selected
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default CrewTravelUpdateMultipleModal;
